<template>
    <div class="wrapper" :class="{ 'wrapper-scrollable': isScrollable }">
        <div v-if="isScrollable" class="document-navigator__arrow">
            <navigate-button
                left
                :is-basic="isBasicNavigateButton"
                :disabled="leftButtonDisabled"
                @click="onLeftScrollerClick"
            />
        </div>
        <ul
            class="v-pagination"
            :class="{
                'v-pagination--scrollable': isScrollable,
            }"
        >
            <li v-if="firstPageVisible" class="v-pagination__first-page">
                <v-btn
                    density="comfortable"
                    size="small"
                    :variant="isActiveClass(firstPageValue) ? 'flat' : 'outlined'"
                    :disabled="isPageDisabled(firstPageValue)"
                    :color="isActiveClass(firstPageValue) ? 'primary' : ''"
                    class="v-pagination__item"
                    :class="{
                        'v-pagination__item--active': isActiveClass(firstPageValue),
                        'v-pagination__item--colored': coloredPages.includes(firstPageValue),
                        'v-pagination__item--highlighted': highlightedPages.includes(
                            firstPageValue
                        ),
                    }"
                    @click.prevent="pageClickHandler(1)"
                >
                    {{ firstPageValue }}
                </v-btn>
            </li>
            <li
                v-if="isPaginationEllipsisVisible({ isStart: true })"
                class="v-pagination__ellipsis--start"
            >
                ...
            </li>
            <li
                v-for="(page, index) in computedLength"
                :key="'page' + page"
                :class="{ 'no-border': isBorderNeed(index) }"
            >
                <v-btn
                    size="small"
                    :variant="isActiveClass(getPageValue(page)) ? 'flat' : 'outlined'"
                    :disabled="isPageDisabled(getPageValue(page))"
                    :color="isActiveClass(getPageValue(page)) ? 'primary' : ''"
                    class="v-pagination__item"
                    :class="{
                        'v-pagination__item--active': isActiveClass(getPageValue(page)),
                        'v-pagination__item--colored': coloredPages.includes(getPageValue(page)),
                        'v-pagination__item--highlighted': highlightedPages.includes(
                            getPageValue(page)
                        ),
                    }"
                    @click.prevent="pageClickHandler(getPageValue(page))"
                >
                    {{ getPageValue(page) }}
                </v-btn>
            </li>
            <li
                v-if="isPaginationEllipsisVisible({ isStart: false })"
                class="v-pagination__ellipsis--end"
            >
                ...
            </li>
            <li v-if="lastPageVisible" class="v-pagination__last-page">
                <v-btn
                    density="comfortable"
                    size="small"
                    :variant="isActiveClass(length) ? 'flat' : 'outlined'"
                    :color="isActiveClass(length) ? 'primary' : ''"
                    :disabled="isPageDisabled(length)"
                    class="v-pagination__item"
                    :class="{
                        'v-pagination__item--active': isActiveClass(length),
                        'v-pagination__item--colored': coloredPages.includes(length),
                        'v-pagination__item--highlighted': highlightedPages.includes(length),
                    }"
                    @click.prevent="pageClickHandler(length)"
                >
                    {{ length }}
                </v-btn>
            </li>
        </ul>
        <div v-if="isScrollable" class="document-navigator__arrow">
            <navigate-button
                right
                :is-basic="isBasicNavigateButton"
                :disabled="rightButtonDisabled"
                @click="onRightScrollerClick"
            />
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';

const props = defineProps({
    length: {
        type: Number,
        required: true,
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    modelValue: {
        type: Number,
        default: null,
    },
    value: {
        type: Number,
        default: null,
    },
    coloredPages: {
        type: Array,
        default: () => [],
    },
    disabledPages: {
        type: Array,
        default: () => [],
    },
    highlightedPages: {
        type: Array,
        default: () => [],
    },
    startFromSecond: {
        type: Boolean,
        default: false,
    },
    isMultiSelect: {
        type: Boolean,
        default: false,
    },
    multiSelectValues: {
        type: Array,
        default: () => [],
    },
    itemsInScreen: {
        type: Number,
        default: 10,
    },
    isBasicNavigateButton: {
        type: Boolean,
        default: false,
    },
    useArrowAsPagination: {
        type: Boolean,
        default: false,
    },
});
const emit = defineEmits(['update:modelValue', 'input']);

const offset = ref(0);

const currentValue = computed(() => (props.modelValue !== null ? props.modelValue : props.value));
const isScrollable = computed(() =>
    props.startFromSecond
        ? props.itemsInScreen < props.length - 1
        : props.itemsInScreen < props.length
);
const computedLength = computed(() => {
    if (!isScrollable.value && props.startFromSecond) {
        return props.length - 1;
    }
    return isScrollable.value ? props.itemsInScreen : props.length;
});
const maxOffset = computed(() => {
    if (props.length < props.itemsInScreen) {
        return 0;
    }
    if (props.startFromSecond) {
        return props.length - props.itemsInScreen - 1;
    }
    return props.length - props.itemsInScreen;
});
const rightButtonDisabled = computed(() => offset.value === maxOffset.value);
const leftButtonDisabled = computed(() => {
    if (props.useArrowAsPagination) {
        return currentValue.value === 1;
    }
    return offset.value === 0;
});
const firstPageVisible = computed(() => isScrollable.value && offset.value > 0);
const lastPageVisible = computed(() => isScrollable.value && offset.value < maxOffset.value);
const firstPageValue = computed(() => (props.startFromSecond ? 2 : 1));

watch(
    () => props.length,
    () => {
        offset.value = 0;
    }
);

const pageClickHandler = page => {
    if (page === 1) {
        offset.value = 0;
    }
    if (page === props.length) {
        offset.value = maxOffset.value;
    }
    emit('update:modelValue', page);
    emit('input', page);
};
const isPageDisabled = page => props.disabled || props.disabledPages.includes(page);
const onLeftScrollerClick = () => {
    if (offset.value > 0) {
        offset.value -= 1;
    }

    if (props.useArrowAsPagination) {
        const next = currentValue.value - 1;
        emit('update:modelValue', next);
        emit('input', next);
    }
};
const onRightScrollerClick = () => {
    if (props.useArrowAsPagination) {
        const next = currentValue.value + 1;
        emit('update:modelValue', next);
        emit('input', next);

        if (currentValue.value >= props.itemsInScreen) {
            offset.value += 1;
        }
    } else if (offset.value < maxOffset.value) {
        offset.value += 1;
    }
};

// in case of copy resource we need to skip first page, prop startFromSecond is using for this
// for this reason this method was added
// ex: we need render pages from 2 to 8
// in prop we have length === 8 for rendering needed pages we decrease length for 1
// and add 1 for each page
// also we can use offset for supporting scrolling
const getPageValue = page =>
    props.startFromSecond ? page + 1 + offset.value : page + offset.value;

const isActiveClass = page => {
    if (!props.isMultiSelect) {
        return currentValue.value === page;
    }

    return props.multiSelectValues.includes(page);
};
const isBorderNeed = index => (props.startFromSecond && index === 0) || offset.value > 0;
const isPaginationEllipsisVisible = ({ isStart }) => {
    if (isStart) {
        return offset.value > 0;
    }
    return isScrollable.value && offset.value < maxOffset.value;
};
</script>

<style scoped lang="scss">
@import '@style/base/_variables.scss';
.wrapper-scrollable {
    display: grid;
    grid-template-columns: 2.7rem auto 2.7rem;
    align-items: start;
}

.wrapper-pagination {
    width: 18.3rem;
    overflow: hidden;
    position: relative;
    margin-left: 1rem;
}

.v-pagination {
    align-items: start;
    display: inline-flex;
    gap: 0.6rem;
    list-style-type: none;
    justify-content: center;
    margin: 0;
    max-width: 100%;

    width: auto;

    li.no-border {
        border: none;
        padding: 0;
    }

    li.no-border + li {
        padding: 0;
    }

    .v-pagination {
        &__item {
            min-width: 2rem;
            width: 2rem;
            height: 2rem !important;
            padding: 0;
            // Embedded, Assortment's unscoped VPagination CSS (margin: 0.3rem) can outrank Vuetify's reset.
            margin: 0;
            border-radius: 0.2rem;
            border: $component-border;
            color: $promo-primary-colour;

            &.v-pagination__item--active {
                color: $promo-white;
            }

            &--colored {
                color: $positive-action;
                border-color: $positive-action;

                &.v-pagination__item--active {
                    background-color: $positive-action !important;
                    color: $promo-white;
                }
            }

            &--highlighted {
                background-color: $highlighted-category-bg-colour !important;
            }

            &:focus {
                outline: none;
            }
            &:focus::before {
                opacity: 0 !important;
            }
        }

        &__item.bg-primary {
            padding: 0 1.2rem !important;
        }

        &__ellipsis {
            &--start,
            &--end {
                height: 2.6rem;
                letter-spacing: 0.2rem;
                padding: 0 0.4rem !important;
                font-weight: bold;
            }

            &--start {
                border-left: $component-border-divider;
            }

            &--end {
                border-right: $component-border-divider;
            }
        }

        &__first-page {
            padding-right: 0.4rem;
        }

        &__last-page {
            padding-left: 0.4rem;
        }
    }
    &.v-pagination--scrollable {
        margin-left: 0.5rem;
        padding-left: 0.4rem;
    }
}
</style>
